import {
  fetchOfflineBadgeManifest,
  syncOfflineGateScansBatch,
  syncOfflinePerkRedemptionsBatch,
  OfflineBadgeManifestResponse,
  OfflineManifestEntry,
} from './api';

export interface OfflineGateScanResult {
  granted: boolean;
  anomaly: boolean;
  status?: string;
  isReplaced?: boolean;
  isDuplicate?: boolean;
  offlineValidated: boolean;
  message: string;
  attendee?: Record<string, any>;
  theme?: Record<string, any>;
}

const DB_NAME = 'iiab_offline_scanner_db';
const DB_VERSION = 1;
const STORE_MANIFEST = 'manifest';
const STORE_GATE_QUEUE = 'gate_scan_queue';
const STORE_PERK_QUEUE = 'perk_redeem_queue';

const LS_MANIFEST_KEY = 'iiab_offline_manifest_v1';
const LS_GATE_QUEUE_KEY = 'iiab_offline_gate_queue_v1';
const LS_PERK_QUEUE_KEY = 'iiab_offline_perk_queue_v1';

export interface QueuedGateScan {
  clientScanId: string;
  code: string;
  location: string;
  scanType: 'door_entry' | 'hall_entry' | 'keynote_entry';
  eventDay: 'build_up' | 'day1' | 'day2' | 'dismantle';
  scannedAt: string;
}

export interface QueuedPerkRedemption {
  clientRedemptionId: string;
  perkId: string;
  identifier: string;
  vendorCounterId: string;
  vendorName: string;
  redemptionDate: string;
  notes?: string;
  redeemedAt: string;
}

function openIdb(): Promise<IDBDatabase | null> {
  if (typeof window === 'undefined' || !('indexedDB' in window) || !window.indexedDB) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    try {
      const req = window.indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE_MANIFEST)) {
          db.createObjectStore(STORE_MANIFEST, { keyPath: 'id' });
        }
        if (!db.objectStoreNames.contains(STORE_GATE_QUEUE)) {
          db.createObjectStore(STORE_GATE_QUEUE, { keyPath: 'clientScanId' });
        }
        if (!db.objectStoreNames.contains(STORE_PERK_QUEUE)) {
          db.createObjectStore(STORE_PERK_QUEUE, { keyPath: 'clientRedemptionId' });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

export async function saveManifestToCache(manifest: OfflineBadgeManifestResponse): Promise<void> {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(LS_MANIFEST_KEY, JSON.stringify(manifest));
    }
  } catch {
    // ignore storage quota errors
  }

  const db = await openIdb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_MANIFEST, 'readwrite');
      tx.objectStore(STORE_MANIFEST).put({ id: 'latest', ...manifest });
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export async function loadManifestFromCache(): Promise<OfflineBadgeManifestResponse | null> {
  const db = await openIdb();
  if (db) {
    const idbData = await new Promise<OfflineBadgeManifestResponse | null>((resolve) => {
      try {
        const tx = db.transaction(STORE_MANIFEST, 'readonly');
        const req = tx.objectStore(STORE_MANIFEST).get('latest');
        req.onsuccess = () => resolve(req.result || null);
        req.onerror = () => resolve(null);
      } catch {
        resolve(null);
      }
    });
    if (idbData && Array.isArray(idbData.entries)) {
      return idbData;
    }
  }

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(LS_MANIFEST_KEY);
      if (raw) return JSON.parse(raw);
    }
  } catch {
    // ignore
  }
  return null;
}

export async function refreshOfflineScannerManifest(
  scannerKey?: string
): Promise<OfflineBadgeManifestResponse | null> {
  try {
    const manifest = await fetchOfflineBadgeManifest(scannerKey);
    if (manifest && Array.isArray(manifest.entries)) {
      await saveManifestToCache(manifest);
      return manifest;
    }
  } catch {
    // Fallback to existing cached manifest when offline
  }
  return loadManifestFromCache();
}

export async function getQueuedGateScans(): Promise<QueuedGateScan[]> {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(LS_GATE_QUEUE_KEY);
      if (raw) return JSON.parse(raw);
    }
  } catch {
    // ignore
  }
  return [];
}

async function saveQueuedGateScans(queue: QueuedGateScan[]): Promise<void> {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(LS_GATE_QUEUE_KEY, JSON.stringify(queue));
    }
  } catch {
    // ignore
  }
}

export async function enqueueOfflineGateScan(scan: Omit<QueuedGateScan, 'clientScanId' | 'scannedAt'>): Promise<QueuedGateScan> {
  const item: QueuedGateScan = {
    ...scan,
    clientScanId: `offline-gate-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    scannedAt: new Date().toISOString(),
  };
  const queue = await getQueuedGateScans();
  queue.push(item);
  await saveQueuedGateScans(queue);
  return item;
}

export async function getQueuedPerkRedemptions(): Promise<QueuedPerkRedemption[]> {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(LS_PERK_QUEUE_KEY);
      if (raw) return JSON.parse(raw);
    }
  } catch {
    // ignore
  }
  return [];
}

async function saveQueuedPerkRedemptions(queue: QueuedPerkRedemption[]): Promise<void> {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(LS_PERK_QUEUE_KEY, JSON.stringify(queue));
    }
  } catch {
    // ignore
  }
}

export async function enqueueOfflinePerkRedemption(
  item: Omit<QueuedPerkRedemption, 'clientRedemptionId' | 'redeemedAt'>
): Promise<QueuedPerkRedemption> {
  const queued: QueuedPerkRedemption = {
    ...item,
    clientRedemptionId: `offline-perk-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    redeemedAt: new Date().toISOString(),
  };
  const queue = await getQueuedPerkRedemptions();
  queue.push(queued);
  await saveQueuedPerkRedemptions(queue);
  return queued;
}

function findManifestEntry(
  manifest: OfflineBadgeManifestResponse,
  rawCode: string
): { entry: OfflineManifestEntry; isReplaced: boolean } | null {
  const clean = rawCode.trim().toUpperCase();
  for (const e of manifest.entries) {
    if (
      (e.badgeCode && e.badgeCode.toUpperCase() === clean) ||
      (e.nfcTagId && e.nfcTagId.toUpperCase() === clean) ||
      (e.qrToken && e.qrToken.toUpperCase() === clean) ||
      (e.collectionCode && e.collectionCode.toUpperCase() === clean) ||
      (e.email && e.email.toUpperCase() === clean) ||
      e.attendeeId.toUpperCase() === clean ||
      (Array.isArray(e.allBadgeCodes) && e.allBadgeCodes.some((c) => c.toUpperCase() === clean))
    ) {
      const isReplaced =
        Array.isArray(e.ReplacedBadgeCodes) &&
        e.ReplacedBadgeCodes.some((c) => c.toUpperCase() === clean);
      return { entry: e, isReplaced };
    }
  }
  return null;
}

/**
 * Validates a Gate Turnstile scan locally in <50ms using the cached IndexedDB manifest
 * and queues the scan event for automatic background sync when Wi-Fi returns.
 */
export async function validateOfflineGateScan(params: {
  code: string;
  location: string;
  scanType: 'door_entry' | 'hall_entry' | 'keynote_entry';
  eventDay: 'build_up' | 'day1' | 'day2' | 'dismantle';
}): Promise<OfflineGateScanResult> {
  const manifest = await loadManifestFromCache();
  if (!manifest || !Array.isArray(manifest.entries) || manifest.entries.length === 0) {
    return {
      granted: false,
      anomaly: true,
      offlineValidated: true,
      message: 'Offline mode active, but badge manifest is not cached yet. Connect briefly to Wi-Fi to sync roster.',
    };
  }

  const matched = findManifestEntry(manifest, params.code);
  if (!matched) {
    return {
      granted: false,
      anomaly: true,
      offlineValidated: true,
      message: 'Credential not recognized in offline cache. Direct participant to Registration Desk, Pavilion 3.',
    };
  }

  const { entry, isReplaced } = matched;

  if (entry.isUnpaid) {
    return {
      granted: false,
      anomaly: true,
      status: 'payment_pending',
      offlineValidated: true,
      message: 'Payment is not completed for this registration. Please visit the Helpdesk.',
    };
  }

  const queuedScans = await getQueuedGateScans();
  const alreadyScannedOffline = queuedScans.some(
    (q) => q.code.trim().toUpperCase() === params.code.trim().toUpperCase() && q.eventDay === params.eventDay
  );
  const isDuplicate = Boolean(entry.checkedInAt) || alreadyScannedOffline;

  await enqueueOfflineGateScan(params);

  // Mark checkedInAt in cached manifest
  if (!entry.checkedInAt) {
    entry.checkedInAt = new Date().toISOString();
    await saveManifestToCache(manifest);
  }

  return {
    granted: true,
    anomaly: isReplaced || isDuplicate,
    isReplaced,
    isDuplicate,
    offlineValidated: true,
    message: isReplaced
      ? '[OFFLINE CACHE] Flagged: Replaced badge used. Ingress granted.'
      : isDuplicate
      ? '[OFFLINE CACHE] Pass Re-Entry Verified (Queued for Sync)'
      : '[OFFLINE CACHE] Pass Verified: Ingress Granted (Queued for Sync)',
    attendee: {
      id: entry.attendeeId,
      fullName: entry.fullName,
      companyName: entry.companyName,
      jobTitle: entry.jobTitle,
      primaryRole: entry.primaryRole as any,
      ticketType: entry.ticketType,
      tier: entry.tier,
      avatarUrl: entry.avatarUrl,
      checkedInAt: entry.checkedInAt,
    },
    theme: entry.theme,
  };
}

function evaluateOfflinePerkEligibility(perk: any, entry: OfflineManifestEntry, checkDate: string) {
  if (perk.validDate && perk.validDate !== checkDate) {
    return { eligible: false, reason: `Valid only on ${perk.validDate}` };
  }
  const criteria = perk.eligibilityCriteria || {};
  if (Array.isArray(criteria.attendeeIds) && criteria.attendeeIds.length > 0) {
    if (!criteria.attendeeIds.includes(entry.attendeeId)) {
      return { eligible: false, reason: 'Attendee not in specific invite list' };
    }
    return { eligible: true };
  }

  let hasRule = false;
  let matchesTier = false;
  let matchesRole = false;

  if (Array.isArray(criteria.tiers) && criteria.tiers.length > 0) {
    hasRule = true;
    if (criteria.tiers.includes(entry.tier)) matchesTier = true;
  }
  if (Array.isArray(criteria.roles) && criteria.roles.length > 0) {
    hasRule = true;
    if ((entry.roles || []).some((r) => criteria.roles.includes(r)) || criteria.roles.includes(entry.primaryRole)) {
      matchesRole = true;
    }
  }
  if (!hasRule || matchesTier || matchesRole) {
    return { eligible: true };
  }
  return {
    eligible: false,
    reason: `Requires eligible tier (${(criteria.tiers || []).join(', ') || 'N/A'}) or role (${(criteria.roles || []).join(', ') || 'N/A'})`,
  };
}

/**
 * Looks up a participant's F&B and Lounge perks locally in <50ms when the Perks Counter tablet is offline.
 */
export async function lookupOfflineBadgePerks(identifier: string, date?: string) {
  const manifest = await loadManifestFromCache();
  if (!manifest || !Array.isArray(manifest.entries)) {
    throw new Error('Offline badge manifest not cached yet. Connect briefly to Wi-Fi to download.');
  }

  const matched = findManifestEntry(manifest, identifier);
  if (!matched) {
    throw new Error(`No attendee found in offline cache matching '${identifier}'.`);
  }

  const { entry } = matched;
  const checkDate = date || new Date().toISOString().split('T')[0];
  const queuedRedemptions = await getQueuedPerkRedemptions();

  const evaluatedPerks = (manifest.perks || []).map((p: any) => {
    const eligibility = evaluateOfflinePerkEligibility(p, entry, checkDate);
    const serverReds = (manifest.redemptions || []).filter((r) => {
      if (r.attendeeId !== entry.attendeeId || r.perkId !== p.id) return false;
      return p.perkType === 'daily_wallet' ? r.redemptionDate === checkDate : true;
    });
    const localReds = queuedRedemptions.filter((q) => {
      if (q.perkId !== p.id) return false;
      const qMatch = findManifestEntry(manifest, q.identifier);
      if (!qMatch || qMatch.entry.attendeeId !== entry.attendeeId) return false;
      return p.perkType === 'daily_wallet' ? q.redemptionDate === checkDate : true;
    });

    const redeemedCount = serverReds.length + localReds.length;
    const maxRedemptions = Number(p.maxRedemptionsPerAttendee) || 1;
    const remainingRedemptions = Math.max(0, maxRedemptions - redeemedCount);
    const isRedeemed = redeemedCount >= maxRedemptions;

    let status: 'available' | 'already_redeemed' | 'ineligible' = 'available';
    if (!eligibility.eligible) status = 'ineligible';
    else if (isRedeemed) status = 'already_redeemed';

    const lastLocal = localReds[localReds.length - 1];
    const lastServer = serverReds[serverReds.length - 1];

    return {
      id: p.id,
      code: p.code,
      name: p.name,
      description: p.description,
      perkType: p.perkType,
      unitValueInr: p.unitValueInr,
      validDate: p.validDate,
      sponsorName: p.sponsorName,
      sponsorTier: p.sponsorTier,
      maxRedemptions,
      redeemedCount,
      remainingRedemptions,
      status,
      ineligibilityReason: eligibility.eligible ? undefined : eligibility.reason,
      lastRedemption: lastLocal
        ? {
            redeemedAt: lastLocal.redeemedAt,
            vendorName: `${lastLocal.vendorName} (Queued Offline)`,
            vendorCounterId: lastLocal.vendorCounterId,
          }
        : lastServer || null,
    };
  });

  return {
    attendee: {
      id: entry.attendeeId,
      fullName: entry.fullName,
      companyName: entry.companyName,
      jobTitle: entry.jobTitle,
      email: entry.email,
      passTier: entry.tier,
      badgeStatus: 'active',
      roles: entry.roles,
      badgeCode: entry.badgeCode,
      nfcTagId: entry.nfcTagId,
    },
    checkDate,
    perks: evaluatedPerks,
    offlineMode: true,
  };
}

/**
 * Redeems a perk voucher locally in <50ms when offline and queues it for automatic batch sync.
 */
export async function redeemOfflinePerkVoucher(payload: {
  perkId: string;
  identifier: string;
  vendorCounterId: string;
  vendorName: string;
  redemptionDate?: string;
  notes?: string;
}) {
  const effectiveDate = payload.redemptionDate || new Date().toISOString().split('T')[0];
  const lookup = await lookupOfflineBadgePerks(payload.identifier, effectiveDate);
  const targetPerk = lookup.perks.find((p: any) => p.id === payload.perkId);

  if (!targetPerk) {
    throw new Error('Perk not found in offline cache.');
  }
  if (targetPerk.status === 'ineligible') {
    throw new Error(targetPerk.ineligibilityReason || 'Attendee is not eligible for this perk.');
  }
  if (targetPerk.status === 'already_redeemed') {
    throw new Error(`Voucher '${targetPerk.name}' has already been redeemed.`);
  }

  const queued = await enqueueOfflinePerkRedemption({
    perkId: payload.perkId,
    identifier: payload.identifier,
    vendorCounterId: payload.vendorCounterId,
    vendorName: payload.vendorName,
    redemptionDate: effectiveDate,
    notes: payload.notes,
  });

  return {
    success: true,
    offlineQueued: true,
    redemptionId: queued.clientRedemptionId,
    perk: {
      id: targetPerk.id,
      code: targetPerk.code,
      name: targetPerk.name,
      unitValueInr: targetPerk.unitValueInr,
      sponsorName: targetPerk.sponsorName,
    },
    attendee: {
      id: lookup.attendee.id,
      fullName: lookup.attendee.fullName,
      companyName: lookup.attendee.companyName,
    },
    vendor: {
      vendorName: payload.vendorName,
      vendorCounterId: payload.vendorCounterId,
    },
    redeemedAt: queued.redeemedAt,
    remainingRedemptions: Math.max(0, targetPerk.remainingRedemptions - 1),
  };
}

/**
 * Flushes both queued Gate Turnstile scans and Perks Counter redemptions when connectivity returns.
 */
export async function flushOfflineScannerQueues(scannerKey?: string): Promise<{
  syncedGateScans: number;
  syncedPerkRedemptions: number;
}> {
  let syncedGateScans = 0;
  let syncedPerkRedemptions = 0;

  const gateQueue = await getQueuedGateScans();
  if (gateQueue.length > 0) {
    try {
      const res = await syncOfflineGateScansBatch(gateQueue, scannerKey);
      if (res.success) {
        syncedGateScans = res.syncedCount;
        await saveQueuedGateScans([]);
      }
    } catch {
      // Keep in queue for next online retry
    }
  }

  const perkQueue = await getQueuedPerkRedemptions();
  if (perkQueue.length > 0) {
    try {
      const res = await syncOfflinePerkRedemptionsBatch(perkQueue, scannerKey);
      if (res.success) {
        syncedPerkRedemptions = res.syncedCount;
        await saveQueuedPerkRedemptions([]);
      }
    } catch {
      // Keep in queue for next online retry
    }
  }

  if (syncedGateScans > 0 || syncedPerkRedemptions > 0) {
    await refreshOfflineScannerManifest(scannerKey);
  }

  return { syncedGateScans, syncedPerkRedemptions };
}
